import { useEffect, useState } from 'react';
import { ApiOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { Alert, App, Button, Form, Input, InputNumber, Select, Space, Switch, Tag } from 'antd';
import { api } from '../../api';
import { DataTable, ErrorNotice, PageHeader, Panel } from '../../components';
import { formatDateTime, useLoad } from '../../lib';
import { BackToSettings } from './BackToSettings';

const PRODUCTS = [
    { key: 'gst', title: 'GST API', description: 'GSTIN and HSN lookup, GST portal sessions, returns, statements and ledgers.' },
    { key: 'einvoice', title: 'e-Invoice API', description: 'IRN generation, lookup, cancellation and signed QR details.' },
    { key: 'ewaybill', title: 'e-Way Bill API', description: 'Generation, lookup, Part B, transporter, extension and cancellation.' }
];
const CAPABILITY_LABEL = {
    validate: 'Validate GSTIN', taxpayer_lookup: 'Taxpayer lookup', hsn_lookup: 'HSN/SAC lookup', returns_read: 'Read returns',
    returns_file: 'File returns', gstr2a_download: 'Download GSTR-2A', gstr2b_download: 'Download GSTR-2B',
    gstr4a_download: 'Download GSTR-4A', ledger_read: 'Read GST ledgers', einvoice: 'e-Invoice', ewaybill: 'e-Way Bill'
};
const PRODUCT_CAPABILITIES = {
    gst: ['validate', 'taxpayer_lookup', 'hsn_lookup', 'returns_read', 'returns_file', 'gstr2a_download', 'gstr2b_download', 'gstr4a_download', 'ledger_read'],
    einvoice: ['einvoice'],
    ewaybill: ['ewaybill']
};
const fields = ['clientId', 'clientSecret', 'email', 'ipAddress'];

function ProductConfiguration({ product, configuration, priority, onReload }) {
    const { message, modal } = App.useApp();
    const [form] = Form.useForm();
    const [busy, setBusy] = useState('');
    const data = configuration?.products?.[product.key] ?? {};
    const credentials = data.credentials ?? {};
    const health = data.health ?? {};
    useEffect(() => {
        form.setFieldsValue({
            email: credentials.email ?? '', ipAddress: credentials.ipAddress ?? '', clientId: '', clientSecret: '',
            priority, paths: JSON.stringify(data.paths ?? {}, null, 2)
        });
    }, [configuration, credentials.email, credentials.ipAddress, data.paths, form, priority]);

    async function save(values) {
        setBusy('save');
        try {
            let paths;
            try {
                paths = values.paths?.trim() ? JSON.parse(values.paths) : {};
            } catch {
                throw new Error('Endpoint mappings must be valid JSON.');
            }
            await api('/v1/admin/gst/providers/authorized/config', {
                method: 'PUT',
                body: JSON.stringify({
                    product: product.key,
                    priority: values.priority,
                    credentials: Object.fromEntries(fields.map((key) => [key, values[key] ?? ''])),
                    paths
                })
            });
            message.success(`${product.title} production configuration saved`);
            await onReload();
        } catch (error) {
            message.error(error instanceof Error ? error.message : `Could not save ${product.title}`);
        } finally {
            setBusy('');
        }
    }

    async function test() {
        setBusy('test');
        try {
            const result = await api('/v1/admin/gst/providers/authorized/test', { method: 'POST', body: JSON.stringify({ product: product.key }) });
            message.success(`${product.title} connected in ${result.durationMs} ms`);
        } catch (error) {
            message.error(error instanceof Error ? error.message : `${product.title} connection failed`);
        } finally {
            await onReload();
            setBusy('');
        }
    }

    function clear() {
        modal.confirm({
            title: `Clear ${product.title} credentials?`,
            content: `Only ${product.title} calls will stop. The other WhiteBooks APIs keep their separate configurations.`,
            okText: 'Clear credentials', okButtonProps: { danger: true },
            onOk: async () => {
                await api('/v1/admin/gst/providers/authorized/config', {
                    method: 'PUT', body: JSON.stringify({ product: product.key, clearFields: fields })
                });
                message.success(`${product.title} credentials cleared`);
                await onReload();
            }
        });
    }

    return (<Panel title={product.title} description={product.description} extra={<Space>
      <Tag color="blue">Production only</Tag>
      <Tag color={health.ok ? 'success' : 'warning'}>{health.ok ? 'Connected' : 'Not ready'}</Tag>
    </Space>}>
      <Alert type={health.ok ? 'success' : 'warning'} showIcon style={{ marginBottom: 18 }}
        title={health.ok ? 'Production connection verified' : 'This API is not ready'}
        description={health.testedAt ? `${health.message} Last tested ${formatDateTime(health.testedAt)}.` : 'Save this API’s issued credentials, then test its production connection.'}/>
      <Form form={form} layout="vertical" requiredMark={false} onFinish={save}>
        <div className="form-row">
          <Form.Item name="email" label="WhiteBooks account email" rules={[{ required: true, type: 'email' }]}><Input placeholder="name@company.com" autoComplete="off" /></Form.Item>
          <Form.Item name="ipAddress" label="Allow-listed server IP" rules={[{ required: true, whitespace: true }]}><Input placeholder="203.0.113.10" className="mono" autoComplete="off" /></Form.Item>
        </div>
        <div className="form-row">
          <Form.Item name="clientId" label="Client ID" extra={credentials.clientIdSet ? 'Saved; leave blank to keep it.' : ''}><Input.Password placeholder="Client ID from WhiteBooks" className="mono" autoComplete="new-password" /></Form.Item>
          <Form.Item name="clientSecret" label="Client secret" extra={credentials.clientSecretSet ? 'Saved; leave blank to keep it.' : ''}><Input.Password placeholder="Client secret from WhiteBooks" className="mono" autoComplete="new-password" /></Form.Item>
        </div>
        {product.key === 'gst' && <div className="form-row">
          <Form.Item name="priority" label="Provider priority" extra="Lower numbers are tried first."><InputNumber placeholder="1" min={1} max={100} style={{ width: '100%' }} /></Form.Item>
        </div>}
        <Form.Item name="paths" label={`${product.title} endpoint mappings`} extra="Production paths from the WhiteBooks collection. Do not put credentials in this JSON.">
          <Input.TextArea placeholder='{ "search": "/public/search" }' className="mono" rows={product.key === 'gst' ? 10 : 7} />
        </Form.Item>
        <Space wrap>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={busy === 'save'}>Save {product.title}</Button>
          <Button icon={<ApiOutlined />} onClick={test} loading={busy === 'test'}>Test production connection</Button>
          <Button danger icon={<DeleteOutlined />} onClick={clear}>Clear credentials</Button>
        </Space>
      </Form>
    </Panel>);
}

export function WhiteBooksSettings() {
    const { message, modal } = App.useApp();
    const providers = useLoad(() => api('/v1/admin/gst/providers'));
    const configuration = useLoad(() => api('/v1/admin/gst/providers/authorized/config'));
    const overrides = useLoad(() => api('/v1/admin/gst/providers/authorized/accounts').then((body) => body.overrides));
    const accounts = useLoad(() => api('/v1/admin/accounts?q=').then((body) => body.accounts));
    const [saving, setSaving] = useState('');
    const [accountQuery, setAccountQuery] = useState('');
    const provider = providers.data?.providers?.find((row) => row.id === 'authorized');
    const allowed = providers.data?.allowed?.authorized ?? [];
    const overrideMap = new Map((overrides.data ?? []).map((row) => [row.accountId, row.capabilities]));

    async function updateProvider(change, confirmed = false) {
        if (change.enabled === true && !provider?.enabled && !confirmed) {
            modal.confirm({
                title: 'Enable WhiteBooks production?',
                content: 'Every selected API must have a successful production connection test. Enabled actions will call the live WhiteBooks services.',
                okText: 'Enable production', okButtonProps: { danger: true }, onOk: () => updateProvider(change, true)
            });
            return;
        }
        setSaving('provider');
        try {
            await api('/v1/admin/gst/providers/authorized', {
                method: 'PUT', body: JSON.stringify({ enabled: change.enabled ?? provider.enabled, capabilities: change.capabilities ?? provider.capabilities, settings: provider.settings })
            });
            message.success('WhiteBooks access saved');
            await providers.reload();
        } catch (error) {
            message.error(error instanceof Error ? error.message : 'Could not save WhiteBooks access');
        } finally { setSaving(''); }
    }

    async function saveAccount(accountId, capabilities) {
        setSaving(accountId);
        try {
            await api(`/v1/admin/gst/providers/authorized/accounts/${accountId}`, { method: 'PUT', body: JSON.stringify({ capabilities }) });
            message.success('WhiteBooks access saved for this account');
            await overrides.reload();
        } catch (error) {
            message.error(error instanceof Error ? error.message : 'Could not save account access');
        } finally { setSaving(''); }
    }

    const reload = () => Promise.all([configuration.reload(), providers.reload()]);
    return (<>
      <PageHeader title="WhiteBooks APIs" description="Three independent production API configurations for GST, e-Invoice and e-Way Bill. Shared secrets are encrypted and never returned by the backend." actions={<BackToSettings />}/>
      <Alert type="warning" showIcon title="Production services only" description="There is no sandbox configuration. Connection tests and enabled actions use WhiteBooks production. Taxpayer portal usernames, passwords and OTPs are never saved here." style={{ marginBottom: 20 }}/>
      <Alert type="info" showIcon title="Live data only" description="Customer calls never fall back to stored GSTIN or HSN responses. A failed WhiteBooks call is shown as an error and is not charged." style={{ marginBottom: 20 }}/>
      <ErrorNotice error={providers.error || configuration.error}/>
      <Panel title="Global access" description="Select what Kivonex may use. A product cannot be activated until its own production credentials pass testing.">
        <div className="form-row">
          <div><strong>WhiteBooks provider</strong><span className="cell-sub">Master switch for all three APIs</span></div>
          <Switch checked={Boolean(provider?.enabled)} checkedChildren="On" unCheckedChildren="Off" loading={saving === 'provider'} onChange={(enabled) => updateProvider({ enabled })}/>
        </div>
        <div style={{ marginTop: 18 }}>
          <div style={{ marginBottom: 8 }}><strong>Enabled capabilities</strong></div>
          <Select placeholder="Choose capabilities" mode="multiple" style={{ width: '100%' }} value={provider?.capabilities ?? []} loading={providers.loading || saving === 'provider'}
            options={PRODUCTS.map((product) => ({ label: product.title, title: product.title, options: PRODUCT_CAPABILITIES[product.key].filter((capability) => allowed.includes(capability)).map((capability) => ({ value: capability, label: CAPABILITY_LABEL[capability] })) }))}
            onChange={(capabilities) => updateProvider({ capabilities })}/>
        </div>
      </Panel>
      {PRODUCTS.map((product) => <ProductConfiguration key={product.key} product={product} configuration={configuration.data}
        priority={provider?.settings?.priority ?? 10} onReload={reload}/>) }
      <Panel title="Customer account access" description="Read operations default on; filing, e-Invoice and e-Way Bill remain off until explicitly enabled for the account." flush>
        <ErrorNotice error={accounts.error || overrides.error}/>
        <Input.Search allowClear placeholder="Search customer account" onChange={(event) => setAccountQuery(event.target.value)} style={{ maxWidth: 360, margin: 12 }}/>
        <DataTable rowKey="id" loading={accounts.loading || overrides.loading} dataSource={(accounts.data ?? []).filter((row) => !row.disabledAt && (!accountQuery.trim() || `${row.name} ${row.email} ${row.mobile}`.toLowerCase().includes(accountQuery.trim().toLowerCase())))} pagination={{ pageSize: 20, hideOnSinglePage: true }} columns={[
            { title: 'Account', render: (_value, row) => <span><strong>{row.name}</strong><span className="cell-sub">{row.email || row.mobile}</span></span> },
            { title: 'Explicit capabilities', render: (_value, row) => <Select placeholder="Uses provider defaults" mode="multiple" style={{ width: '100%', minWidth: 320 }} value={Object.entries(overrideMap.get(row.id) ?? {}).filter(([, on]) => on).map(([key]) => key)} options={allowed.map((capability) => ({ value: capability, label: CAPABILITY_LABEL[capability] ?? capability }))} loading={saving === row.id} onChange={(next) => saveAccount(row.id, Object.fromEntries(allowed.map((capability) => [capability, next.includes(capability)])))}/> },
        ]}/>
      </Panel>
      <Panel title="Credential model" description="Matches the current WhiteBooks product separation.">
        <p style={{ margin: 0, lineHeight: 1.7 }}>GST, e-Invoice and e-Way Bill each keep their own account email, client ID, client secret and allow-listed server IP. Runtime GST portal OTP sessions and e-Invoice/e-Way Bill portal credentials remain transient and are not stored by Kivonex Cloud.</p>
      </Panel>
    </>);
}
